import React, { useMemo } from 'react';

import { useOfferService } from 'services/OfferService';

import {
  CardWrapper,
  CheckboxesSection,
  EligibilityLink,
  GiftIcon,
  HeaderSection,
  InstructionsHeading,
  InstructionsSection,
  InstructionsText,
  LabelText,
  StyledCheckboxLabeledInput,
  StyledFormCheckbox,
  StyledFormError,
  Title,
  TopContainer,
} from './OfferAcknowledgementCard-styled-components';

interface OfferAcknowledgementCardProps {
  acceptsTerms: boolean;
  acceptsGiftCardOfferedByOpencare: boolean;
  onAcceptTermsChange: (checked: boolean) => void;
  onAcceptGiftCardOfferedByOpencareChange: (checked: boolean) => void;
  showValidationErrors: boolean;
  formValidity: {
    acceptsTerms?: { message?: string; };
    acceptsGiftCardOfferedByOpencare?: { message?: string; };
  };
  onEligibilityRequirementsClick: () => void;
}

export const OfferAcknowledgementCard = ({
  acceptsTerms,
  acceptsGiftCardOfferedByOpencare,
  onAcceptTermsChange,
  onAcceptGiftCardOfferedByOpencareChange,
  showValidationErrors,
  formValidity,
  onEligibilityRequirementsClick,
}: OfferAcknowledgementCardProps) => {
  const { formattedReferralOfferName, offerName, offerValue } = useOfferService();

  const offerNameToDisplay = useMemo(
    () => {
      if (formattedReferralOfferName) {
        return formattedReferralOfferName;
      }

      return offerName;
    },
    [formattedReferralOfferName, offerName],
  );

  const offerTermsLabel = useMemo(
    () => (
      <LabelText>
        I understand I must complete a new patient exam and cleaning at this practice to qualify for the{' '}
        <strong>${offerValue} gift card</strong>.
      </LabelText>
    ),
    [offerValue],
  );

  const offerOfferedByOpencareLabel = useMemo(
    () => (
      <LabelText>
        I understand this gift card is <strong>offered by Opencare</strong>, not the dental practice. {' '}
        For any questions, I will contact Opencare.
      </LabelText>
    ),
    [],
  );

  return (
    <CardWrapper>
      <TopContainer>
        <HeaderSection>
          <GiftIcon />
          <Title>{offerNameToDisplay}</Title>
        </HeaderSection>

        <CheckboxesSection>
          <StyledCheckboxLabeledInput label={offerTermsLabel} htmlFor="offer-terms-card">
            <StyledFormCheckbox
              id="offer-terms-card"
              checked={acceptsTerms}
              onChange={(e) => onAcceptTermsChange(e.target.checked)}
            />
          </StyledCheckboxLabeledInput>
          {showValidationErrors && formValidity.acceptsTerms?.message && (
            <StyledFormError>{formValidity.acceptsTerms.message}</StyledFormError>
          )}

          <StyledCheckboxLabeledInput label={offerOfferedByOpencareLabel} htmlFor="offer-opencare-card">
            <StyledFormCheckbox
              id="offer-opencare-card"
              checked={acceptsGiftCardOfferedByOpencare}
              onChange={(e) => onAcceptGiftCardOfferedByOpencareChange(e.target.checked)}
            />
          </StyledCheckboxLabeledInput>
          {showValidationErrors && formValidity.acceptsGiftCardOfferedByOpencare?.message && (
            <StyledFormError>{formValidity.acceptsGiftCardOfferedByOpencare.message}</StyledFormError>
          )}
        </CheckboxesSection>
      </TopContainer>

      <InstructionsSection>
        <InstructionsHeading>How to get your ${offerValue} gift card reward</InstructionsHeading>
        <InstructionsText>
          Complete your new patient exam and cleaning, then submit your receipt to Opencare after your visit. Review the full{' '}
          <EligibilityLink onClick={onEligibilityRequirementsClick}>
            eligibility requirements
          </EligibilityLink>
          .
        </InstructionsText>
      </InstructionsSection>
    </CardWrapper>
  );
};

